/**
 * StructuredDataViewer.jsx
 * 
 * Component for viewing processed documents and their structured data.
 * Shows extraction results, quality metrics, and allows data export.
 */

import React, { useState, useEffect } from 'react';
import './StructuredDataViewer.css';

const StructuredDataViewer = ({ auditorId, engagementId, documents, onNewUpload }) => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [viewMode, setViewMode] = useState('overview'); // 'overview', 'sections', 'tables', 'chunks'
  const [structuredData, setStructuredData] = useState(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [documentLoadError, setDocumentLoadError] = useState(null);

  useEffect(() => {
    if (documents && documents.length > 0 && !selectedDocument) {
      handleDocumentSelect(documents[0]);
    }
  }, [documents]);

  const handleDocumentSelect = async (document) => {
    setIsLoadingDocument(true);
    setDocumentLoadError(null);
    setViewMode('overview');
    
    try {
      // Fetch detailed document data including document_structure
      const response = await fetch(
        `/api/v1/document-intake/document/${document.document_id}?auditor_id=${auditorId}&engagement_id=${engagementId}`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to load document details: ${response.status}`);
      }
      
      const detailedDocument = await response.json();
      setSelectedDocument(detailedDocument);
    } catch (error) {
      console.error('Error loading document details:', error);
      setDocumentLoadError(error.message);
      // Fallback to basic document data from list
      setSelectedDocument(document);
    } finally {
      setIsLoadingDocument(false);
    }
  };

  const getQualityColor = (score) => {
    if (score >= 0.8) return '#27ae60'; // Green
    if (score >= 0.6) return '#f39c12'; // Orange
    return '#e74c3c'; // Red
  };

  const getQualityLabel = (score) => {
    if (score >= 0.8) return 'High';
    if (score >= 0.6) return 'Medium';
    return 'Low';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const exportStructuredData = async () => {
    try {
      const response = await fetch(
        `/api/v1/document-intake/structured-data?auditor_id=${auditorId}&engagement_id=${engagementId}`
      );
      
      if (response.ok) {
        const data = await response.json();
        
        // Create and download JSON file
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `structured_data_${engagementId}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const renderOverview = () => (
    <div className="overview-content">
      <div className="document-stats">
        <div className="stat-card">
          <div className="stat-number">{selectedDocument.document_structure?.metadata?.total_pages || selectedDocument.total_pages || 0}</div>
          <div className="stat-label">Pages</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{selectedDocument.document_structure?.metadata?.total_sections || selectedDocument.total_sections || 0}</div>
          <div className="stat-label">Sections</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{selectedDocument.document_structure?.metadata?.total_tables || selectedDocument.total_tables || 0}</div>
          <div className="stat-label">Tables</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{selectedDocument.llm_optimized_chunks?.length || 0}</div>
          <div className="stat-label">Chunks</div>
        </div>
      </div>

      <div className="quality-metrics">
        <h3>Quality Assessment</h3>
        <div className="quality-grid">
          <div className="quality-item">
            <span className="quality-label">Extraction Completeness</span>
            <div className="quality-bar">
              <div 
                className="quality-fill"
                style={{ 
                  width: `${selectedDocument.quality_assessment.extraction_completeness * 100}%`,
                  backgroundColor: getQualityColor(selectedDocument.quality_assessment.extraction_completeness)
                }}
              ></div>
            </div>
            <span className="quality-score">
              {Math.round((selectedDocument.extraction_completeness || 0) * 100)}%
            </span>
          </div>
          
          <div className="quality-item">
            <span className="quality-label">Structure Confidence</span>
            <div className="quality-bar">
              <div 
                className="quality-fill"
                style={{ 
                  width: `${(selectedDocument.structure_confidence || 0) * 100}%`,
                  backgroundColor: getQualityColor(selectedDocument.structure_confidence || 0)
                }}
              ></div>
            </div>
            <span className="quality-score">
              {Math.round((selectedDocument.structure_confidence || 0) * 100)}%
            </span>
          </div>
          
          <div className="quality-item">
            <span className="quality-label">OCR Quality</span>
            <div className="quality-bar">
              <div 
                className="quality-fill"
                style={{ 
                  width: `${(selectedDocument.quality_assessment?.ocr_quality || 0) * 100}%`,
                  backgroundColor: getQualityColor(selectedDocument.quality_assessment?.ocr_quality || 0)
                }}
              ></div>
            </div>
            <span className="quality-score">
              {Math.round((selectedDocument.quality_assessment?.ocr_quality || 0) * 100)}%
            </span>
          </div>
        </div>

        {/* Processing flags temporarily disabled - will be re-enabled with quality_assessments table */}
      </div>

      <div className="processing-info">
        <h3>Processing Information</h3>
        <div className="info-grid">
          <div className="info-row">
            <span className="info-label">Document Title:</span>
            <span className="info-value">{selectedDocument.document_structure?.metadata?.document_title || selectedDocument.filename}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Processing Time:</span>
            <span className="info-value">
              {selectedDocument.processing_metadata?.processing_time 
                ? `${selectedDocument.processing_metadata.processing_time.toFixed(2)}s` 
                : 'N/A'}
            </span>
          </div>
          <div className="info-row">
            <span className="info-label">Processed At:</span>
            <span className="info-value">
              {selectedDocument.processing_metadata?.processed_at 
                ? new Date(selectedDocument.processing_metadata.processed_at).toLocaleString() 
                : 'N/A'}
            </span>
          </div>
          <div className="info-row">
            <span className="info-label">Pipeline Version:</span>
            <span className="info-value">{selectedDocument.processing_metadata?.pipeline_version || '1.0.0'}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSections = () => {
    const sections = selectedDocument.document_structure?.sections || [];
    return (
      <div className="sections-content">
        <h3>Document Sections ({sections.length})</h3>
        {sections.length === 0 ? (
          <p>No sections found or document details not loaded.</p>
        ) : (
          <div className="sections-list">
            {sections.map((section, index) => (
          <div key={index} className="section-item">
            <div className="section-header">
              <span className="section-level">Level {section.level}</span>
              <h4>{section.heading}</h4>
              <span className="section-pages">Pages {section.page_start}-{section.page_end}</span>
            </div>
            <div className="section-content">
              {section.content.substring(0, 300)}
              {section.content.length > 300 && '...'}
            </div>
          </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTables = () => {
    const tables = selectedDocument.document_structure?.tables || [];
    return (
      <div className="tables-content">
        <h3>Extracted Tables ({tables.length})</h3>
        {tables.length === 0 ? (
          <p>No tables found or document details not loaded.</p>
        ) : (
          <div className="tables-list">
            {tables.map((table, index) => (
            <div key={index} className="table-item">
              <div className="table-header">
                <h4>{table.title}</h4>
                <span className="table-page">Page {table.page}</span>
              </div>
              <div className="table-preview">
                <table>
                  <thead>
                    <tr>
                      {table.headers.map((header, hIndex) => (
                        <th key={hIndex}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.slice(0, 3).map((row, rIndex) => (
                      <tr key={rIndex}>
                        {row.map((cell, cIndex) => (
                          <td key={cIndex}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {table.rows.length > 3 && (
                  <p className="table-more">... and {table.rows.length - 3} more rows</p>
                )}
              </div>
            </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderChunks = () => {
    const chunks = selectedDocument.llm_optimized_chunks || [];
    return (
      <div className="chunks-content">
        <h3>LLM-Optimized Chunks ({chunks.length})</h3>
        {chunks.length === 0 ? (
          <p>No chunks found or document details not loaded.</p>
        ) : (
          <div className="chunks-list">
            {chunks.map((chunk, index) => (
          <div key={index} className="chunk-item">
            <div className="chunk-header">
              <span className="chunk-id">{chunk.chunk_id}</span>
              <span className="chunk-tokens">{chunk.token_count} tokens</span>
              <span className="chunk-pages">Pages: {chunk.page_references.join(', ')}</span>
            </div>
            <div className="chunk-context">{chunk.context}</div>
            <div className="chunk-content">
              {chunk.content.substring(0, 400)}
              {chunk.content.length > 400 && '...'}
            </div>
          </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!documents || documents.length === 0) {
    return (
      <div className="no-documents">
        <div className="no-documents-content">
          <div className="no-documents-icon">📄</div>
          <h3>No Documents Processed</h3>
          <p>Upload documents to see structured data extraction results.</p>
          <button className="upload-button" onClick={onNewUpload}>
            Upload Documents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="structured-data-viewer">
      <div className="viewer-header">
        <h2>Structured Data Results</h2>
        <div className="header-actions">
          <button className="export-button" onClick={exportStructuredData}>
            📥 Export All Data
          </button>
          <button className="new-upload-button" onClick={onNewUpload}>
            ➕ Upload More
          </button>
        </div>
      </div>

      <div className="viewer-content">
        {/* Document List Sidebar */}
        <div className="document-sidebar">
          <h3>Documents ({documents.length})</h3>
          <div className="document-list">
            {documents.map((doc, index) => (
              <div 
                key={index}
                className={`document-item ${selectedDocument?.document_id === doc.document_id ? 'selected' : ''}`}
                onClick={() => handleDocumentSelect(doc)}
              >
                <div className="document-name">
                  {doc.filename || `Document ${index + 1}`}
                </div>
                <div className="document-meta">
                  <span className="quality-badge" style={{ backgroundColor: getQualityColor(doc.extraction_completeness || 0) }}>
                    {getQualityLabel(doc.extraction_completeness || 0)}
                  </span>
                  <span>{doc.total_pages || 0} pages</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="document-content">
          {selectedDocument && (
            <>
              <div className="content-header">
                <h3>{selectedDocument.filename || 'Untitled Document'}</h3>
                <div className="view-tabs">
                  <button 
                    className={`tab ${viewMode === 'overview' ? 'active' : ''}`}
                    onClick={() => setViewMode('overview')}
                  >
                    Overview
                  </button>
                  <button 
                    className={`tab ${viewMode === 'sections' ? 'active' : ''}`}
                    onClick={() => setViewMode('sections')}
                  >
                    Sections ({selectedDocument.document_structure?.sections?.length || 0})
                  </button>
                  <button 
                    className={`tab ${viewMode === 'tables' ? 'active' : ''}`}
                    onClick={() => setViewMode('tables')}
                  >
                    Tables ({selectedDocument.document_structure?.tables?.length || 0})
                  </button>
                  <button 
                    className={`tab ${viewMode === 'chunks' ? 'active' : ''}`}
                    onClick={() => setViewMode('chunks')}
                  >
                    Chunks ({selectedDocument.llm_optimized_chunks?.length || 0})
                  </button>
                </div>
              </div>

              <div className="content-body">
                {isLoadingDocument ? (
                  <div className="loading-state">
                    <p>Loading document details...</p>
                  </div>
                ) : documentLoadError ? (
                  <div className="error-state">
                    <p>Error loading document details: {documentLoadError}</p>
                    <p>Showing basic information only.</p>
                  </div>
                ) : (
                  <>
                    {viewMode === 'overview' && renderOverview()}
                    {viewMode === 'sections' && renderSections()}
                    {viewMode === 'tables' && renderTables()}
                    {viewMode === 'chunks' && renderChunks()}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StructuredDataViewer;
